import axios from 'axios';

import { authToken } from '../../scripts/authentication.script';

export const PRESIGNED_UPLOAD_THRESHOLD_BYTES = 9 * 1024 * 1024 // 9 MB — API Gateway limit is 10 MB
export const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB
export const MAX_ZIP_SIZE_BYTES = 200 * 1024 * 1024 // 200 MB

export interface UploadResult {
  status: number
  data: any
}

export class MediaDA {
  public static async getDocuments(
    id_company: string,
    status: string,
    sort_field: string,
    sort_mode: string,
    page: number,
    limit: number,
    search?: string,
    dated_from?: string,
    dated_to?: string,
    uploaded_from?: string,
    uploaded_to?: string,
    content_type?: string,
    ocr_status?: string,
    lifecycle_view?: 'active' | 'ignored' | 'archived' | 'all'
  ): Promise<any> {
    try {
      const params: any = { page, limit: Math.min(limit, 100) }
      if (status) params.status = status
      if (sort_field) params.sort_by = sort_field
      if (sort_mode) params.sort_order = sort_mode
      if (search && search.trim().length > 0) params.search = search.trim().slice(0, 200)
      if (dated_from) params.dated_from = dated_from
      if (dated_to) params.dated_to = dated_to
      if (uploaded_from) params.uploaded_from = uploaded_from
      if (uploaded_to) params.uploaded_to = uploaded_to
      if (content_type) params.content_type = content_type
      if (ocr_status) params.ocr_status = ocr_status
      if (lifecycle_view) params.lifecycle_view = lifecycle_view

      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents`
      let config: any = {
        headers: { Authorization: authToken() },
        params
      }

      const response = await axios.get(url, config)
      const data = response.data?.data ?? response.data
      return { status: 200, data: { items: data.items ?? [], pagination: data.pagination ?? { limit, total: 0, page: 1, pages: 0 } } }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async getDocument(
    id_company: string,
    id_document: string
  ): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.get(url, config)
      const data = response.data?.data ?? response.data
      return { status: 200, data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async getDocumentSignedUrl(
    id_company: string,
    id_document_inbox: string
  ): Promise<string | null> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document_inbox}`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.get(url, config)
      const data = response.data?.data ?? response.data
      return data?.signed_url ?? null
    } catch (_error) {
      return null
    }
  }

  public static async postDocument(
    id_company: string,
    formData: FormData,
    source: 'mobile' | 'web' | 'desktop' = 'web'
  ): Promise<any> {
    try {
      formData.append('source', source)
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents`
      let config: any = {
        headers: {
          Authorization: authToken(),
          'Content-Type': 'multipart/form-data'
        }
      }

      const response = await axios.post(url, formData, config)
      return { status: 201, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async putDocument(
    id_company: string,
    id_document: string,
    body: any
  ): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.put(url, body, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async putAssignment(
    id_company: string,
    id_document: string,
    assigned_type: string,
    assigned_id: string
  ): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}/assignment`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.put(url, { assigned_type, assigned_id }, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteDocument(
    id_company: string,
    id_document: string
  ): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async ignoreDocument(id_company: string, id_document: string): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}/ignore`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, {}, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async unignoreDocument(id_company: string, id_document: string): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}/unignore`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, {}, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async archiveDocument(id_company: string, id_document: string): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}/archive`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, {}, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async unarchiveDocument(id_company: string, id_document: string): Promise<any> {
    try {
      const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/${id_document}/unarchive`
      const config = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, {}, config)
      return { status: 200, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async attachVoucher(
    id_company: string,
    entity_slug: string,
    id_entity: string,
    id_document_inbox: string
  ): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/${entity_slug}/${id_entity}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }

      const response = await axios.post(url, { id_document_inbox }, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  // --- Presigned upload flow ---

  private static async uploadInit(
    id_company: string,
    fileName: string,
    contentType: string,
    fileSize: number,
    source: string = 'web'
  ): Promise<any> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/upload/init`
    const config = { headers: { Authorization: authToken() } }
    const response = await axios.post(url, { file_name: fileName, content_type: contentType, file_size: fileSize, source }, config)
    return response.data?.data ?? response.data
  }

  private static async uploadFinalize(
    id_company: string,
    uploadId: string
  ): Promise<any> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/upload/finalize`
    const config = { headers: { Authorization: authToken() } }
    const response = await axios.post(url, { upload_id: uploadId }, config)
    return response.data?.data ?? response.data
  }

  private static async zipUploadInit(
    id_company: string,
    fileName: string,
    contentType: string,
    fileSize: number,
    source: string = 'web'
  ): Promise<any> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/zip-upload/init`
    const config = { headers: { Authorization: authToken() } }
    const response = await axios.post(url, { file_name: fileName, content_type: contentType, file_size: fileSize, source }, config)
    return response.data?.data ?? response.data
  }

  private static async zipUploadFinalize(
    id_company: string,
    uploadId: string
  ): Promise<any> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/media/documents/zip-upload/finalize`
    const config = { headers: { Authorization: authToken() } }
    const response = await axios.post(url, { upload_id: uploadId }, config)
    return response.data?.data ?? response.data
  }

  private static async uploadToS3(
    presignedUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': contentType },
      transformRequest: [(data, headers) => {
        if (headers) delete headers['Authorization']
        return data
      }],
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total))
        }
      }
    })
  }

  private static async uploadDocumentPresigned(
    id_company: string,
    file: File,
    source: string = 'web',
    onProgress?: (percent: number) => void
  ): Promise<UploadResult> {
    try {
      const initData = await MediaDA.uploadInit(id_company, file.name, file.type || 'application/octet-stream', file.size, source)
      const presignedUrl = initData?.presigned_url ?? initData?.url
      const uploadId = initData?.upload_id ?? initData?.id

      if (!presignedUrl || !uploadId) {
        return { status: 500, data: { message: 'Missing presigned URL or upload ID from init' } }
      }

      await MediaDA.uploadToS3(presignedUrl, file, file.type || 'application/octet-stream', onProgress)

      const finalData = await MediaDA.uploadFinalize(id_company, uploadId)
      return { status: 201, data: finalData }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? { message: error.message } }
    }
  }

  private static async uploadZipPresigned(
    id_company: string,
    file: File,
    source: string = 'web',
    onProgress?: (percent: number) => void
  ): Promise<UploadResult> {
    try {
      const initData = await MediaDA.zipUploadInit(id_company, file.name, file.type || 'application/zip', file.size, source)
      const presignedUrl = initData?.presigned_url ?? initData?.url
      const uploadId = initData?.upload_id ?? initData?.id

      if (!presignedUrl || !uploadId) {
        return { status: 500, data: { message: 'Missing presigned URL or upload ID from init' } }
      }

      await MediaDA.uploadToS3(presignedUrl, file, file.type || 'application/zip', onProgress)

      const finalData = await MediaDA.zipUploadFinalize(id_company, uploadId)
      // ZIP extraction is async — 202 signals background processing
      return { status: 202, data: finalData }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? { message: error.message } }
    }
  }

  public static async uploadDocumentSmart(
    id_company: string,
    file: File,
    source: 'mobile' | 'web' | 'desktop' = 'web',
    onProgress?: (percent: number) => void
  ): Promise<UploadResult> {
    const isZip = file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || file.name.toLowerCase().endsWith('.zip')
    const maxSize = isZip ? MAX_ZIP_SIZE_BYTES : MAX_DOCUMENT_SIZE_BYTES

    if (file.size > maxSize) {
      return { status: 413, data: { message: 'File too large' } }
    }

    // Small files: use existing multipart upload (no presigned needed)
    if (file.size <= PRESIGNED_UPLOAD_THRESHOLD_BYTES) {
      const formData = new FormData()
      formData.append('file', file)
      return MediaDA.postDocument(id_company, formData, source)
    }

    // Large files: use presigned S3 upload
    if (isZip) {
      return MediaDA.uploadZipPresigned(id_company, file, source, onProgress)
    }

    return MediaDA.uploadDocumentPresigned(id_company, file, source, onProgress)
  }
}
